import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, CARD_SECTION_TITLE_CLASS } from '@safetyhub/ui';
import {
  dataExportApi,
  type DownloadMethod,
  type ExportStatus,
  type UserExportScope,
} from '../../lib/organizations';
import { extractApiError } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';

/**
 * 전체 데이터 내보내기 (설정 > 계정) — 결정 기록 `2026-08-22-full-data-export-user-stories.md`
 *
 * 이 카드가 존재하는 이유는 **비OWNER가 탈퇴하지 않고서는 자기 데이터를 받을 방법이
 * 없었다**는 것이다(E1). 조직 설정 화면은 비OWNER를 즉시 리다이렉트하므로, 그쪽을 안내로
 * 대신할 수 없다.
 */
export default function DataExportCard() {
  const { user } = useAuthStore();
  const { showToast } = useToastStore();

  // 수신처는 **계정에 등록된 값**만 쓴다(E11 · S27 수신자 축). 화면에서 임의 주소를 받지
  // 않으므로, 채널이 없으면 그 선택지를 열지 않는 것이 유일하게 정직한 표시다.
  const accountPhone = user?.phone ?? null;
  const accountEmail = user?.email ?? null;

  const [scopes, setScopes] = useState<UserExportScope[] | null>(null);
  const [isLoadingScopes, setIsLoadingScopes] = useState(true);
  // ⚠️ 조회 **실패**와 정상 **무소속**을 반드시 구분한다.
  // 예전엔 실패도 `setScopes([])`로 뭉개서, 네트워크 오류 한 번에 조직이 여럿인 사용자가
  // "내보낼 자료가 없습니다"를 보고 버튼까지 사라졌다 — 이 카드의 존재 이유가 조용히
  // 무효화되는 실패다(2026-08-22 리뷰).
  const [loadFailed, setLoadFailed] = useState(false);
  // 서버가 이미 판정해 준 값을 그대로 쓴다. `scopes.length`로 다시 파생하면 두 축이 갈려,
  // 눌리는 버튼이 `DATA_EXPORT_NO_TARGET` 400을 받는 상태가 생긴다.
  const [canExport, setCanExport] = useState(false);
  const [method, setMethod] = useState<DownloadMethod>('direct');
  const [isRequesting, setIsRequesting] = useState(false);
  // 진행 상태만 들고 있으면 된다 — exportId는 폴링 클로저가 인자로 받아 쓰므로
  // 렌더에 필요하지 않다.
  const [status, setStatus] = useState<ExportStatus | null>(null);

  // 폴링 타이머는 ref로 잡는다 — state로 잡으면 cleanup이 자기 렌더 시점의 값을 보게 되어
  // 언마운트 때 최신 타이머가 남는다.
  const pollRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const loadScopes = useCallback(() => {
    setIsLoadingScopes(true);
    setLoadFailed(false);
    let cancelled = false;
    dataExportApi
      .getUserExportScopes()
      .then((res) => {
        if (cancelled) return;
        setScopes(res.organizations);
        setCanExport(res.canExport);
      })
      .catch(() => {
        if (cancelled) return;
        setScopes(null);
        setCanExport(false);
        setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingScopes(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => loadScopes(), [loadScopes]);

  useEffect(() => stopPolling, [stopPolling]);

  const handleDownload = useCallback(
    async (id: string) => {
      try {
        const blob = await dataExportApi.downloadFile(id);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `safetyhub_export_${id}.zip`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        // 즉시 revoke하면 일부 브라우저(Firefox)가 다운로드를 취소한다 — 한 박자 뒤에 푼다.
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } catch {
        showToast('파일을 내려받지 못했습니다. 잠시 후 다시 시도해 주세요.', 'error');
      }
    },
    [showToast],
  );

  /** 완료 통지 — direct는 즉시 받고, 링크 방식은 어디로 갔는지 알린다. */
  const announceReady = useCallback(
    async (id: string, chosenMethod: DownloadMethod) => {
      if (chosenMethod === 'direct') {
        await handleDownload(id);
        showToast('내보내기가 완료되어 다운로드를 시작했습니다.', 'success');
        return;
      }
      showToast(
        chosenMethod === 'email'
          ? '내보내기가 완료되어 등록된 이메일로 다운로드 링크를 보냈습니다.'
          : '내보내기가 완료되어 등록된 번호로 다운로드 링크를 보냈습니다.',
        'success',
      );
    },
    [handleDownload, showToast],
  );

  const startPolling = useCallback(
    (id: string, chosenMethod: DownloadMethod) => {
      stopPolling();
      pollRef.current = window.setInterval(async () => {
        try {
          const res = await dataExportApi.getExportStatus(id);
          setStatus(res.status);

          // ⚠️ **GENERATING이 아니면 무조건 멈춘다.** 예전엔 READY·FAILED만 처리해,
          // EXPIRED/DELETED/DOWNLOADED가 오면 어느 분기도 안 타 2초 폴링이 영원히 돌고
          // 화면은 "준비 중"에 멈춘 채 아무 설명이 없었다. CI가 web 타입을 안 보므로
          // 이런 비exhaustive 분기는 아무도 잡아 주지 않는다.
          if (res.status === 'GENERATING') return;

          stopPolling();

          if (res.status === 'READY' || res.status === 'DOWNLOADED') {
            await announceReady(id, chosenMethod);
          } else if (res.status === 'FAILED') {
            showToast('내보내기 생성에 실패했습니다. 다시 시도해 주세요.', 'error');
          } else {
            // EXPIRED · DELETED — 다시 요청하면 된다.
            showToast('내보내기가 만료되었습니다. 다시 요청해 주세요.', 'error');
          }
        } catch {
          // 일시적 실패는 다음 회차에 다시 묻는다 — 여기서 멈추면 완성된 건을 영영 못 본다.
        }
      }, 2000);
    },
    [announceReady, showToast, stopPolling],
  );

  const handleRequest = async () => {
    setIsRequesting(true);
    try {
      const result = await dataExportApi.createUserExport(method);
      setStatus(result.status);

      if (result.status === 'GENERATING') {
        // 재사용 안내는 **아직 만들어지는 중일 때만** 참이다. 예전엔 status와 무관하게
        // 띄워서, 이미 끝난 건을 돌려받고도 "기다립니다"라고 말했다.
        if (result.reused) {
          showToast('이미 준비 중인 내보내기가 있어 그 결과를 기다립니다.', 'info');
        }
        startPolling(result.exportId, method);
        return;
      }

      // 즉시 완료된 경우 — 예전엔 `direct`만 처리해 email/sms를 고르면 **화면에 아무 일도
      // 일어나지 않았다**(사용자는 반복 클릭하게 된다).
      if (result.status === 'READY' || result.status === 'DOWNLOADED') {
        await announceReady(result.exportId, method);
        return;
      }

      showToast('내보내기를 시작하지 못했습니다. 다시 시도해 주세요.', 'error');
    } catch (err) {
      // ⚠️ 응답 봉투는 `data.error.message`다 — `data.message`는 **항상 undefined**라
      // 서버가 알려 준 사유(내보낼 자료 없음 등)가 사용자에게 닿지 않았다.
      showToast(extractApiError(err).message || '내보내기를 요청하지 못했습니다.', 'error');
    } finally {
      setIsRequesting(false);
    }
  };

  const isGenerating = status === 'GENERATING';

  return (
    <Card padding="xl" radius="lg" elevation="md">
      <h3 className={`${CARD_SECTION_TITLE_CLASS} mb-4`}>데이터 관리</h3>

      <h4 className="text-[14.5px] font-bold tracking-tight text-ink-900 mb-2">
        전체 데이터 내보내기
      </h4>

      {isLoadingScopes ? (
        <p className="text-sm text-ink-500">내보낼 자료를 확인하는 중입니다…</p>
      ) : loadFailed ? (
        /* 실패를 "자료 없음"으로 말하지 않는다 — 사용자가 자기 데이터가 없다고 오해하고
           떠나게 되는 종류의 거짓말이다. 다시 시도할 길을 준다. */
        <div className="text-sm">
          <p className="text-rose-700 mb-2">
            내보낼 자료를 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.
          </p>
          <Button variant="secondary" onClick={loadScopes}>
            다시 확인
          </Button>
        </div>
      ) : !canExport ? (
        <p className="text-sm text-ink-500">
          현재 소속된 조직이 없어 내보낼 자료가 없습니다.
        </p>
      ) : (
        <>
          {/* 무엇이 담기는지 먼저 보인다 — 조직마다 범위가 다르기 때문이다(E3·E4). */}
          <p className="text-sm text-ink-500 mb-2">
            소속된 조직 {scopes!.length}곳의 자료를 한 파일로 묶습니다. 조직마다 폴더가
            나뉩니다.
          </p>
          <ul className="mb-4 space-y-1">
            {scopes!.map((org) => (
              <li key={org.companyId} className="text-sm text-ink-700 flex items-center gap-2">
                <span className="font-medium">{org.organizationName || '(이름 없음)'}</span>
                <span
                  className={
                    org.scope === 'FULL_ORG'
                      ? 'text-[12px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700'
                      : 'text-[12px] px-1.5 py-0.5 rounded bg-ink-100 text-ink-600'
                  }
                >
                  {org.scope === 'FULL_ORG' ? '조직 전체 자료' : '내가 작성한 자료'}
                </span>
              </li>
            ))}
          </ul>

          <fieldset className="mb-4" disabled={isRequesting || isGenerating}>
            <legend className="text-sm font-medium text-ink-700 mb-2">받을 방법</legend>
            <div className="space-y-2">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="user-export-method"
                  className="mt-1"
                  checked={method === 'direct'}
                  onChange={() => setMethod('direct')}
                />
                <span>
                  <span className="text-ink-900">이 화면에서 바로 받기</span>
                  <span className="block text-ink-500">
                    준비가 끝날 때까지 이 페이지를 열어 두세요.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="user-export-method"
                  className="mt-1"
                  disabled={!accountEmail}
                  checked={method === 'email'}
                  onChange={() => setMethod('email')}
                />
                <span>
                  <span className={accountEmail ? 'text-ink-900' : 'text-ink-400'}>
                    이메일로 링크 받기
                  </span>
                  <span className="block text-ink-500">
                    {accountEmail
                      ? `등록된 주소(${accountEmail})로 발송 · 링크 7일간 유효`
                      : '계정에 등록된 이메일이 없어 사용할 수 없습니다.'}
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="user-export-method"
                  className="mt-1"
                  disabled={!accountPhone}
                  checked={method === 'sms'}
                  onChange={() => setMethod('sms')}
                />
                <span>
                  <span className={accountPhone ? 'text-ink-900' : 'text-ink-400'}>
                    문자로 링크 받기
                  </span>
                  <span className="block text-ink-500">
                    {accountPhone
                      ? `등록된 번호(${accountPhone})로 발송 · 링크 7일간 유효`
                      : '계정에 등록된 번호가 없어 사용할 수 없습니다.'}
                  </span>
                </span>
              </label>
            </div>
          </fieldset>

          <Button onClick={handleRequest} disabled={isRequesting || isGenerating}>
            {isGenerating
              ? '준비 중…'
              : isRequesting
                ? '요청하는 중…'
                : '전체 데이터 내보내기'}
          </Button>

          {isGenerating && (
            <p className="mt-2 text-sm text-ink-500">
              자료가 많으면 시간이 걸릴 수 있습니다.
              {method === 'direct'
                ? ' 이 페이지를 열어 두시면 완료 시 자동으로 내려받습니다.'
                : ' 준비가 끝나면 알려드리니 페이지를 닫으셔도 됩니다.'}
            </p>
          )}
        </>
      )}

      <p className="mt-4 text-sm text-ink-500">
        회원 탈퇴 시에는 내 자료를 먼저 내려받는 단계가 함께 제공됩니다.
      </p>
    </Card>
  );
}
